// Enhanced ActionButtons Component with improved accessibility
import React from 'react';
import { FiUpload, FiX } from 'react-icons/fi';
import LoadingSpinner from './LoadingSpinner';

/**
 * Accessible component containing action buttons for the uploader
 * - Improved screen reader support
 * - Enhanced keyboard navigation
 * - Better disabled state handling
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isUploading - Whether files are currently uploading
 * @param {boolean} props.isComplete - Whether upload has completed
 * @param {Function} props.onUpload - Function to handle upload
 * @param {Function} props.onClear - Function to clear all files
 * @param {number} props.fileCount - Number of files to upload
 * @param {number} props.videoCount - Number of video files
 * @param {number} props.videoLimit - Maximum number of videos allowed
 * @returns {JSX.Element} Accessible action buttons component
 */
const ActionButtons = ({
  isUploading,
  isComplete,
  onUpload,
  onClear,
  fileCount,
  videoCount,
  videoLimit = 3,
}) => {
  if (isUploading) {
    return (
      <div className="w-100" role="status" aria-label="Upload in progress" aria-live="polite">
        <LoadingSpinner />
      </div>
    );
  }

  if (isComplete) {
    return null; // CompletionStatus component will be shown instead
  }

  const exceedsVideoLimit = videoCount > videoLimit;
  const hasNoFiles = fileCount === 0;
  const isUploadDisabled = exceedsVideoLimit || hasNoFiles;

  // Generate appropriate reason for disabled state
  const getDisabledReason = () => {
    if (exceedsVideoLimit) {
      return `Cannot upload: Video limit of ${videoLimit} exceeded. Please remove some videos.`;
    }
    if (hasNoFiles) {
      return 'No files selected for upload.';
    }
    return '';
  };

  return (
    <div className="d-flex gap-2 mb-3" role="group" aria-label="File upload actions">
      <button
        onClick={onUpload}
        disabled={isUploadDisabled}
        className={`btn ${exceedsVideoLimit ? 'btn-secondary' : 'ff-button--primary'} 
          d-flex align-items-center justify-content-center gap-2 py-2 px-4 upload-button flex-grow-1 font-size-large`}
        aria-label={`Upload ${fileCount} file${fileCount !== 1 ? 's' : ''}`}
        aria-disabled={isUploadDisabled}
        aria-describedby={isUploadDisabled ? 'upload-disabled-reason' : undefined}>
        <FiUpload size={18} aria-hidden="true" className="icon-transparent size-medium-plus" />
        Upload {fileCount} File{fileCount !== 1 ? 's' : ''}
      </button>

      {/* Hidden explanation for screen readers when upload is disabled */}
      {isUploadDisabled && (
        <div id="upload-disabled-reason" className="visually-hidden">
          {getDisabledReason()}
        </div>
      )}

      <button
        className="btn btn-outline-secondary d-flex align-items-center justify-content-center px-3 font-size-large"
        onClick={onClear}
        title="Clear all files"
        aria-label="Clear all files"
        disabled={isUploading}
        aria-disabled={isUploading}>
        <FiX size={18} aria-hidden="true" className="icon-transparent size-medium-plus" />
      </button>
    </div>
  );
};

export default ActionButtons;
